import { BrandSwitcherUtil } from "../../utilities";

export function initSDSPage() {
    const menuToggler = document.querySelector(".menu-toggler");
    const headerMain = document.querySelector("#header-main");
    const loanMenu = document.querySelector("#loan-menu");

    let selectedLink = null;
    let selectedGoButton = null;

    // Create brand switcher context with shared utilities
    const brandContext = BrandSwitcherUtil.createBrandSwitcherContext();

    function clearSelection() {
        if (selectedGoButton) {
            selectedGoButton.remove();
            selectedGoButton = null;
        }

        if (selectedLink) {
            selectedLink.classList.remove("is-selected");
            selectedLink = null;
        }

        brandContext.restorePreviewTheme();
    }

    function selectLink(link) {
        const subdomain = brandContext.getSubdomainFromLink(link);
        if (!subdomain && subdomain !== brandContext.DEFAULT_SUBDOMAIN) return;

        if (selectedLink === link) {
            return;
        }

        clearSelection();

        selectedLink = link;
        selectedLink.classList.add("is-selected");

        brandContext.setPreviewTheme(subdomain);

        const goButton = document.createElement("button");
        goButton.type = "button";
        goButton.className = "btn small";
        goButton.textContent = "Go";
        goButton.style.marginLeft = "var(--sds-space-4)";

        goButton.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            brandContext.navigateToSubdomain(subdomain);
        });

        link.insertAdjacentElement("beforeend", goButton);
        selectedGoButton = goButton;
    }

    if (loanMenu) {
        const links = Array.from(loanMenu.querySelectorAll("a[href]"));

        links.forEach((link) => {
            link.addEventListener("click", (event) => {
                event.preventDefault();
                selectLink(link);
            });
        });
    }

    if (menuToggler && headerMain) {
        menuToggler.addEventListener("click", () => {
            headerMain.classList.toggle("is-open");
        });
    }

    initTokenContrastValidation();
}

function initTokenContrastValidation(root = document) {
    const swatches = Array.from(root.querySelectorAll("[data-bg-token][data-fg-token]"));

    function resolveTokenColor(tokenName) {
        if (!tokenName) return null;

        const probe = document.createElement("span");
        probe.style.position = "absolute";
        probe.style.left = "-9999px";
        probe.style.top = "-9999px";
        probe.style.visibility = "hidden";
        probe.style.backgroundColor = `var(${tokenName})`;

        document.body.appendChild(probe);

        const resolved = getComputedStyle(probe).backgroundColor;

        probe.remove();

        if (!resolved || resolved === "transparent") {
            return null;
        }

        return resolved.trim();
    }

    function clamp01(value) {
        return Math.min(1, Math.max(0, value));
    }

    function srgbChannelToLinear(value) {
        return value <= 0.04045
            ? value / 12.92
            : Math.pow((value + 0.055) / 1.055, 2.4);
    }

    function linearChannelToSrgb(value) {
        return value <= 0.0031308
            ? 12.92 * value
            : 1.055 * Math.pow(value, 1 / 2.4) - 0.055;
    }

    function parseHexColor(colorValue) {
        const match = colorValue.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (!match) return null;

        let hex = match[1];

        if (hex.length === 3) {
            hex = hex.split("").map((char) => char + char).join("");
        }

        return {
            r: parseInt(hex.slice(0, 2), 16),
            g: parseInt(hex.slice(2, 4), 16),
            b: parseInt(hex.slice(4, 6), 16),
            a: 1
        };
    }

    function parseRgbColor(colorValue) {
        const match = colorValue.match(
            /^rgba?\(\s*([0-9.]+)[,\s]+([0-9.]+)[,\s]+([0-9.]+)(?:[\/,]\s*([0-9.]+))?\s*\)$/i
        );

        if (!match) return null;

        return {
            r: Number(match[1]),
            g: Number(match[2]),
            b: Number(match[3]),
            a: match[4] !== undefined ? Number(match[4]) : 1
        };
    }

    function parseOklabColor(colorValue) {
        const match = colorValue.match(
            /^oklab\(\s*([+-]?[0-9.]+%?)\s+([+-]?[0-9.]+)\s+([+-]?[0-9.]+)(?:\s*\/\s*([0-9.]+))?\s*\)$/i
        );

        if (!match) return null;

        let l = match[1];
        const a = Number(match[2]);
        const b = Number(match[3]);
        const alpha = match[4] !== undefined ? Number(match[4]) : 1;

        if (l.endsWith("%")) {
            l = Number(l.slice(0, -1)) / 100;
        } else {
            l = Number(l);
        }

        return oklabToSrgb({
            l: Number(l),
            a,
            b,
            alpha
        });
    }

    function oklabToSrgb({ l, a, b, alpha = 1 }) {
        const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
        const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
        const s_ = l - 0.0894841775 * a - 1.2914855480 * b;

        const l3 = l_ * l_ * l_;
        const m3 = m_ * m_ * m_;
        const s3 = s_ * s_ * s_;

        const rLinear = +4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3;
        const gLinear = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3;
        const bLinear = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.7076147010 * s3;

        const r = Math.round(clamp01(linearChannelToSrgb(rLinear)) * 255);
        const g = Math.round(clamp01(linearChannelToSrgb(gLinear)) * 255);
        const bOut = Math.round(clamp01(linearChannelToSrgb(bLinear)) * 255);

        return {
            r,
            g,
            b: bOut,
            a: alpha
        };
    }

    function parseColorString(colorValue) {
        return (
            parseHexColor(colorValue) ||
            parseRgbColor(colorValue) ||
            parseOklabColor(colorValue)
        );
    }

    function compositeAgainstWhite(rgb) {
        if (!rgb) return null;

        if (rgb.a === undefined || rgb.a >= 1) {
            return {
                r: rgb.r,
                g: rgb.g,
                b: rgb.b
            };
        }

        const alpha = rgb.a;

        return {
            r: Math.round((rgb.r * alpha) + (255 * (1 - alpha))),
            g: Math.round((rgb.g * alpha) + (255 * (1 - alpha))),
            b: Math.round((rgb.b * alpha) + (255 * (1 - alpha)))
        };
    }

    function toLinear(channel) {
        const value = channel / 255;

        return value <= 0.03928
            ? value / 12.92
            : Math.pow((value + 0.055) / 1.055, 2.4);
    }

    function getLuminance(rgb) {
        return (
            0.2126 * toLinear(rgb.r) +
            0.7152 * toLinear(rgb.g) +
            0.0722 * toLinear(rgb.b)
        );
    }

    function getContrastRatio(background, foreground) {
        const bgLuminance = getLuminance(background);
        const fgLuminance = getLuminance(foreground);

        const lighter = Math.max(bgLuminance, fgLuminance);
        const darker = Math.min(bgLuminance, fgLuminance);

        return (lighter + 0.05) / (darker + 0.05);
    }

    function formatRatio(ratio) {
        return (Math.round(ratio * 100) / 100).toFixed(2);
    }

    function applyComplianceBadge(node, text, background, color, borderColor) {
        if (!node) return;

        node.textContent = text;
        node.style.background = background;
        node.style.color = color;
        node.style.border = `1px solid ${borderColor}`;
        node.style.padding = "0.25rem 0.5rem";
        node.style.borderRadius = "0.25rem";
        node.style.display = "inline-block";
    }

    function markInvalidSwatch(swatch, complianceNode, message) {
        swatch.style.border = "2px solid var(--base-color-status-danger)";
        swatch.dataset.contrastValid = "false";

        applyComplianceBadge(
            complianceNode,
            message,
            "var(--base-color-status-danger-surface)",
            "var(--base-color-on-status-danger-surface)",
            "var(--base-color-status-danger)"
        );
    }

    function markValidSwatch(swatch, complianceNode, ratio) {
        swatch.dataset.contrastValid = "true";

        applyComplianceBadge(
            complianceNode,
            `Contrast ${formatRatio(ratio)}:1 · WCAG 2.2 AA pass`,
            "var(--base-color-status-positive-surface)",
            "var(--base-color-on-status-positive-surface)",
            "var(--base-color-status-positive)"
        );
    }

    swatches.forEach((swatch) => {
        const bgToken = swatch.getAttribute("data-bg-token");
        const fgToken = swatch.getAttribute("data-fg-token");
        const complianceNode = swatch.querySelector(".token-swatch__compliance");

        if (!bgToken || !fgToken) {
            return;
        }

        const resolvedBg = resolveTokenColor(bgToken);
        const resolvedFg = resolveTokenColor(fgToken);

        const bgRgb = compositeAgainstWhite(parseColorString(resolvedBg));
        const fgRgb = compositeAgainstWhite(parseColorString(resolvedFg));

        if (!bgRgb || !fgRgb) {
            markInvalidSwatch(
                swatch,
                complianceNode,
                "Contrast could not be validated. The resolved color format is not supported."
            );
            return;
        }

        const contrastRatio = getContrastRatio(bgRgb, fgRgb);
        const passesAA = contrastRatio >= 4.5;

        swatch.dataset.contrastRatio = formatRatio(contrastRatio);

        if (!passesAA) {
            markInvalidSwatch(
                swatch,
                complianceNode,
                `Contrast ${formatRatio(contrastRatio)}:1 · WCAG 2.2 AA fail`
            );
            return;
        }

        markValidSwatch(swatch, complianceNode, contrastRatio);
    });
}